import React, { useEffect, useState } from 'react';
import { useScenario } from '../context/ScenarioContext';
import { SectionHeader, Callout } from '../components/UI';
import DataFreshnessBanner from '../components/DataFreshnessBanner';
import { CommentButton } from '../components/Comments';
import { api, moneyFull } from '../lib/api';
import { Printer } from 'lucide-react';

export default function CashFlow() {
  const { active, forecastData } = useScenario();
  const [actuals, setActuals] = useState([]);

  useEffect(() => { api.get('/actuals').then((r) => setActuals(r.data)).catch(() => {}); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  if (!forecastData) return <div className="label-editorial">Loading…</div>;

  // Rolling 9-month view: past 3 closed actuals + next 6 forecast months.
  const allForecastMonths = forecastData.forecast.months;
  const forecastMonths = allForecastMonths.slice(0, 6);
  const forecastLabels = new Set(allForecastMonths.map((m) => m.label));
  const closedActuals = actuals
    .filter((a) => !forecastLabels.has(a.month_label))
    .slice(-3);

  // Combined column set: 3 actuals + 6 forecast.
  const allColumns = [
    ...closedActuals.map((a) => ({ kind: 'actual', label: a.month_label, actual: a })),
    ...forecastMonths.map((m) => ({ kind: 'forecast', label: m.label, forecast: m })),
  ];

  // Cash-floor watch: only meaningful for forecast months (we don't track cash balance for closed months yet).
  const minCash = Math.min(...forecastMonths.map((m) => m.cash.ending));
  const lowMonth = forecastMonths.find((m) => m.cash.ending === minCash);

  const DASH = <span className="text-indie-ink/30">—</span>;

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow={`Cash Flow · ${active?.name || ''}`}
        title="Beginning cash → ending cash"
        description="Rolling 9-month view: the 3 most recent closed months plus the next 6 forecast months. Closed-month net income comes from QuickBooks via the Indie Financials sheet — cash-balance tracking only kicks in for forecast months."
        right={
          <div className="flex items-center gap-3 no-print">
            <CommentButton targetType="cashflow" targetId={`cashflow.ending.${active?.id || ''}`} label="Cash trajectory" scenarioId={active?.id} />
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="cashflow-print-btn"
              className="inline-flex items-center gap-2 border-2 border-indie-navy text-indie-navy px-3 py-1.5 text-xs uppercase tracking-wider font-semibold hover:bg-indie-navy hover:text-indie-cream"
            >
              <Printer size={14} /> PDF
            </button>
          </div>
        }
      />

      <DataFreshnessBanner kind="actuals" label="Indie Financials sheet" />

      {minCash < 200000 && lowMonth && (
        <Callout title="Heads up" tone="alert">
          Projected cash dips below the $200K safety floor in <span className="font-semibold">{lowMonth.label}</span>
          {' '}({moneyFull(minCash)}). Consider adjusting owner distributions or ad spend in the Assumptions editor.
        </Callout>
      )}

      <div className="bg-white border-t-4 border-indie-navy overflow-x-auto">
        <table data-testid="cashflow-table" className="w-full text-sm num-tabular">
          <thead>
            <tr className="bg-indie-navy text-indie-cream">
              <th className="text-left px-4 py-3 font-medium tracking-wider uppercase text-xs">Month</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">Beginning cash</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">+ Net income</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">− Owner draws</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">Ending cash</th>
              <th className="text-right px-4 py-3 font-medium tracking-wider uppercase text-xs">vs. $200K floor</th>
            </tr>
          </thead>
          <tbody>
            {allColumns.map((col, i) => {
              const isActual = col.kind === 'actual';
              const rowBg = isActual ? 'bg-indie-evergreen/[0.04]' : (i % 2 === 0 ? 'bg-white' : 'bg-indie-cream');
              if (isActual) {
                const a = col.actual;
                return (
                  <tr key={col.label} className={rowBg}>
                    <td className="px-4 py-2.5 font-medium text-indie-navy">
                      {col.label}
                      <span className="ml-2 text-[9px] tracking-widest text-indie-evergreen uppercase font-semibold">Actual</span>
                    </td>
                    <td className="text-right px-4 py-2.5">{DASH}</td>
                    <td className="text-right px-4 py-2.5 text-indie-evergreen">{moneyFull(a.net_income)}</td>
                    <td className="text-right px-4 py-2.5">{DASH}</td>
                    <td className="text-right px-4 py-2.5">{DASH}</td>
                    <td className="text-right px-4 py-2.5">{DASH}</td>
                  </tr>
                );
              }
              const m = col.forecast;
              const delta = m.cash.ending - 200000;
              return (
                <tr key={col.label} className={rowBg}>
                  <td className="px-4 py-2.5 font-medium text-indie-navy">
                    {col.label}
                    <span className="ml-2 text-[9px] tracking-widest text-indie-cobalt uppercase font-semibold">Forecast</span>
                  </td>
                  <td className="text-right px-4 py-2.5">{moneyFull(m.cash.beginning)}</td>
                  <td className="text-right px-4 py-2.5 text-indie-evergreen">{moneyFull(m.cash.net_income)}</td>
                  <td className="text-right px-4 py-2.5 text-indie-ink/70">{moneyFull(m.cash.owner_distributions)}</td>
                  <td className="text-right px-4 py-2.5 font-semibold text-indie-navy">{moneyFull(m.cash.ending)}</td>
                  <td className={`text-right px-4 py-2.5 ${delta >= 0 ? 'text-indie-evergreen' : 'text-indie-neg-dark font-semibold'}`}>
                    {delta >= 0 ? '+' : ''}{moneyFull(delta)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="px-4 py-2.5 text-[11px] text-indie-ink/50 border-t border-indie-navy/10 leading-relaxed">
          Closed months only show net income — beginning/ending cash isn't tracked there yet (would need QuickBooks cash-balance sync). Forecast cash carries forward beginning + net income − owner draws each month.
        </div>
      </div>
    </div>
  );
}
